<template>
  <LayoutOverlay @closeOverlay="closeModal">
    <OverlayCard
      v-if="showConfirmDelete"
      :backgroundTransparent="true"
      @closeOverlay="showConfirmDelete = false"
    >
      <ConfirmDeleteAttachmentCard
        :buttonText="t('general__text__delete') + ' ' + t('general__data__ticket').toLowerCase()"
        @closeOverlay="showConfirmDelete = false"
        @confirmDelete="deleteSelectedTicket"
      />
    </OverlayCard>
    <main class="ticket-container">
      <EditTicketHeader
        :loadingTicket="loadingTicket"
        @archiveTicket="archiveSelectedTicket"
        @closeEditTicketModal="closeModal"
        @deleteTicket="showConfirmDelete = true"
      />
      <section v-if="loadingTicket" class="ticket-container__loading-spinner">
        <PulseLoader color="#3f46d6" size="16px" />
      </section>
      <section v-else class="ticket-container__section">
        <DividerHorizontal style="margin-bottom: 16px" />
        <EditTicketContent
          class="ticket-container__content"
          :ticketNumber="selectedTicketNumber"
          @selectedTab="selectedTab = $event"
        />
      </section>
      <EditTicketFooter
        v-if="!loadingTicket && selectedTab === 'overview'"
        class="ticket-container__footer"
        :createdDate="selectedTicket?.createdDate ?? ''"
        :lastModifiedDate="selectedTicket?.lastModifiedDate ?? ''"
        @cancelEditTicket="closeModal"
        @updateTicket="updateSelectedTicket"
      />
    </main>
  </LayoutOverlay>
</template>
<script lang="ts" setup>
import { computed, provide, reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import { useGeneralStore } from '@/pinia'
import ConfirmDeleteAttachmentCard from '@/components/features/attachments/ConfirmDeleteAttachmentCard.vue'
import EditTicketContent from '@/components/features/tickets/EditTicketContent.vue'
import EditTicketFooter from '@/components/features/tickets/EditTicketFooter.vue'
import EditTicketHeader from '@/components/features/tickets/EditTicketHeader.vue'
import DividerHorizontal from '@/design-system/atoms/DividerHorizontal.vue'
import LayoutOverlay from '@/design-system/templates/LayoutOverlay.vue'
import OverlayCard from '@/design-system/molecules/OverlayCard.vue'
import PulseLoader from 'vue-spinner/src/PulseLoader.vue'
import { UpdateTicket, archiveTicket, deleteTicket, updateTicket } from '@/services/tickets.service'
import * as Sentry from '@sentry/vue'

interface EMITS {
  (e: 'closeEditTicketModal', val: boolean)
}
const emits = defineEmits<EMITS>()

const { t } = useI18n()
const store = useStore()
const piniaStore = useGeneralStore()

const formData = reactive<UpdateTicket>({
  externalId: null,
  title: null,
  description: null,
  categoryId: null,
  typeId: null,
  priority: null,
  status: null,
  startDate: null,
  dueDate: null
  // assignee: null
})
provide<UpdateTicket>('formDataEditErrand', formData)
const selectedTicket = computed(() => piniaStore.selectedTicket)

const loadingTicket = ref(true)
const selectedTab = ref('overview')
const showConfirmDelete = ref(false)

const selectedTicketNumber = computed(
  () =>
    selectedTicket.value?.externalId ?? selectedTicket.value?.ticketNumber?.toString() ?? undefined
)

function closeModal() {
  piniaStore.setSelectedTicket(null)
  emits('closeEditTicketModal', false)
}
async function archiveSelectedTicket() {
  if (selectedTicket.value)
    try {
      const ticket = await archiveTicket(selectedTicket.value?.id)
      if (ticket) {
        setInfoMessage('success', t('general__data__ticket-archived'))
        // remove archived ticket from store
        const ticketsArray = piniaStore.ticketsList.filter((t) => t.id !== ticket.id)
        piniaStore.setTicketsList(ticketsArray)
        piniaStore.setSelectedTicket(null)
        closeModal()
      }
    } catch (error) {
      Sentry.captureException(error)
      setInfoMessage('error', 'general__data__ticket-archive-failed')
    }
}
async function deleteSelectedTicket() {
  if (selectedTicket.value)
    try {
      const res = await deleteTicket(selectedTicket.value?.id)
      if (res >= 200 && res < 300) {
        setInfoMessage('success', t('general__data__ticket-deleted'))
        // remove archived ticket from store
        const ticketsArray = piniaStore.ticketsList.filter((t) => t.id !== selectedTicket.value?.id)
        piniaStore.setTicketsList(ticketsArray)
        piniaStore.setSelectedTicket(null)
        closeModal()
      }
    } catch (error) {
      Sentry.captureException(error)
      setInfoMessage('error', t('general__data__ticket-delete-failed'))
    }
}
function fillSelectedTicketForm(selectedTicket) {
  formData.externalId = selectedTicket.externalId ?? null
  formData.title = selectedTicket.title ?? null
  formData.description = selectedTicket.description ?? null
  formData.categoryId = selectedTicket.categoryId ?? null
  formData.typeId = selectedTicket.typeId ?? null
  formData.priority = selectedTicket.priority ?? null
  formData.status = selectedTicket.status ?? null
  formData.startDate = selectedTicket.startDate ?? null
  formData.dueDate = selectedTicket.dueDate ?? null
  // formData.assignee = selectedTicket.assignee ?? ''
}
function setInfoMessage(type: string, message: string) {
  store.commit('setInfoMessage', { message, type })
}

async function updateSelectedTicket() {
  // all form fields are optional for update
  if (selectedTicket.value && formData)
    try {
      const ticket = await updateTicket(selectedTicket.value.id, formData)
      if (ticket) {
        setInfoMessage('success', t('general__text__update-succeded'))
        // replace updated ticket in store
        const ticketsArray = piniaStore.ticketsList.map((t) => (t.id === ticket.id ? ticket : t))
        piniaStore.setTicketsList(ticketsArray)
        piniaStore.setSelectedTicket(ticket)
        closeModal()
      }
    } catch (error) {
      Sentry.captureException(error)
      setInfoMessage('error', t('general__text__update-failed'))
    }
}

watch(
  selectedTicket,
  (newValue) => {
    if (newValue) {
      fillSelectedTicketForm(newValue)
    }
    loadingTicket.value = false
  },
  { immediate: true }
)
</script>
<style scoped lang="scss">
@use '@/design-system/design-tokens/variables.scss';
@use '@/design-system/design-tokens/font-mixins.scss';
@use '@/design-system/design-tokens/mixins.scss';
.ticket-container {
  background-color: variables.$color-pure-white;
  border-radius: variables.$border-radius-small;
  display: flex;
  flex-direction: column;
  height: fit-content;
  margin: 32px auto auto;
  max-height: 85vh;
  min-height: 472px; // Todo: consider if this is necessary (nice when loading spinner is shown, but not otherwise)
  overflow: hidden;
  padding: 24px;
  width: 800px;
  @media screen and (max-width: 1024px) {
    border-radius: 0;
    height: calc(100vh - variables.$layout-top-bar-height);
    left: 0;
    margin: auto;
    max-height: unset;
    top: 0; // variables.$layout-top-bar-height;
    transform: unset;
    width: 100vw;
  }
  &__content {
    margin-bottom: auto;
    width: 100%;
  }
  &__footer {
    margin-top: 16px;
  }
  &__loading-spinner {
    margin: auto;
  }
  &__section {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    overflow: auto;
  }
}
</style>
